<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { ActionButton } from '@taler/ui'
import { formatMoney, formatRate, impliedRate } from '@taler/domain'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { AccountPicker } from '@/features/entries'
import { LedgerAvatar } from '@/features/relationships'
import type { TransferView } from '../internal/transfer-view.ts'

const props = defineProps<{
  transfer: TransferView
  sourceLedgerName: string
  isBusy: boolean
}>()

const emit = defineEmits<{ accept: [targetAccountId: string]; reject: [] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')

const accepting = ref(false)
const rejecting = ref(false)
const selectedAccountId = ref<string | undefined>(undefined)
const selectedAccountLabel = ref<string | undefined>(undefined)

const isFx = computed(() => props.transfer.source.currency !== props.transfer.target.currency)
const rate = computed(() =>
  isFx.value ? impliedRate(props.transfer.source, props.transfer.target) : undefined,
)
const rateLabel = computed(() =>
  rate.value === undefined ? undefined : formatRate(rate.value, { locale: currentLocale.value }),
)

const targetText = computed(() => formatMoney(props.transfer.target, { locale: currentLocale.value }))
const sourceText = computed(() => formatMoney(props.transfer.source, { locale: currentLocale.value }))

function startAccepting(): void {
  rejecting.value = false
  accepting.value = true
}

function cancelAccepting(): void {
  accepting.value = false
  selectedAccountId.value = undefined
  selectedAccountLabel.value = undefined
}

function confirmAccept(): void {
  if (selectedAccountId.value === undefined) return
  emit('accept', selectedAccountId.value)
}

function startRejecting(): void {
  accepting.value = false
  rejecting.value = true
}

function cancelRejecting(): void {
  rejecting.value = false
}

function confirmReject(): void {
  emit('reject')
}
</script>

<template>
  <li class="flex flex-col gap-[var(--space-inline)] px-[var(--space-cell)] py-[var(--space-inline)]">
    <div class="flex items-center gap-[var(--space-cell)]">
      <LedgerAvatar :id="transfer.sourceLedgerId" />
      <div class="min-w-0 flex-1">
        <p class="truncate text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)]">
          {{ t('transfers.fromLedger', { ledger: sourceLedgerName }) }}
        </p>
        <p v-if="isFx" class="truncate text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('transfers.fxLeg', { amount: sourceText }) }}
          <template v-if="rateLabel"> · {{ t('transfers.impliedRate', { rate: rateLabel }) }}</template>
        </p>
      </div>

      <MoneyAmount v-if="!accepting && !rejecting" :value="transfer.target" tone="transfer" />

      <template v-if="!accepting && !rejecting">
        <ActionButton variant="quiet" :loading="isBusy" @click="startRejecting">
          {{ t('transfers.reject') }}
        </ActionButton>
        <ActionButton variant="secondary" :loading="isBusy" @click="startAccepting">
          {{ t('transfers.accept') }}
        </ActionButton>
      </template>
    </div>

    <p v-if="rejecting" class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
      {{ t('transfers.rejectConsequence', { ledger: sourceLedgerName }) }}
    </p>
    <div v-if="rejecting" class="flex justify-end gap-[var(--space-inline)]">
      <ActionButton variant="quiet" @click="cancelRejecting">{{ t('transfers.cancel') }}</ActionButton>
      <ActionButton variant="secondary" :loading="isBusy" @click="confirmReject">
        {{ t('transfers.reject') }}
      </ActionButton>
    </div>

    <template v-if="accepting">
      <p class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
        {{
          t('transfers.acceptConsequence', {
            amount: targetText,
            account: selectedAccountLabel ?? t('transfers.theChosenAccount'),
          })
        }}
      </p>
      <div class="flex flex-wrap items-center justify-end gap-x-[var(--space-inline)] gap-y-2">
        <label :for="`deposit-${transfer.id}`" class="sr-only">{{ t('transfers.depositInto') }}</label>
        <AccountPicker
          :id="`deposit-${transfer.id}`"
          class="min-w-[14rem] flex-1"
          :label="t('transfers.depositInto')"
          :model-value="selectedAccountId"
          :ledger-id="transfer.targetLedgerId"
          :filter-class="['ASSET', 'LIABILITY']"
          :currency="transfer.target.currency"
          :accounts-label="t('entries.pickerTransferAccounts')"
          @update:model-value="
            (value) => {
              selectedAccountId = value ?? undefined
              if (value === null || value === undefined) selectedAccountLabel = undefined
            }
          "
          @select="(target) => (selectedAccountLabel = target.leaf)"
        />
        <ActionButton variant="quiet" @click="cancelAccepting">{{ t('transfers.cancel') }}</ActionButton>
        <ActionButton
          variant="secondary"
          :loading="isBusy"
          :disabled="selectedAccountId === undefined"
          @click="confirmAccept"
        >
          {{ t('transfers.accept') }}
        </ActionButton>
      </div>
    </template>
  </li>
</template>
